import React, { useState, useEffect, useRef } from 'react';
import { 
  Plus, Edit2, Trash2, Search, Settings, Upload, Wand2, Loader2, 
  Image as ImageIcon, Battery, Layers, BarChart2, Shield, X, Compass 
} from 'lucide-react';
import { GoogleGenerativeAI } from '@google/generative-ai';

export default function ProductSettings() {
  const [forklifts, setForklifts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // Filters and Pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSeriesFilter, setSelectedSeriesFilter] = useState('ALL');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [newOptionInput, setNewOptionInput] = useState('');
  
  const fileInputRef = useRef(null);
  const aiInputRef = useRef(null);
  
  const emptyForklift = {
    manufacturer: 'Toyota', 
    model: '', 
    series: '', 
    category: 'Reach truck', 
    drive_type: 'Electric', 
    operator_type: 'Seated',
    load_capacity_kg: 0, 
    load_centre_mm: 600, 
    wheelbase_mm: 0, 
    service_weight_kg: 0,
    mast_lowered_mm: 0, 
    free_lift_mm: 0, 
    lift_height_mm: 0, 
    mast_extended_mm: 0,
    overall_length_mm: 0, 
    length_to_fork_face_mm: 0, 
    overall_width_mm: 0, 
    turning_radius_mm: 0,
    aisle_width_800x1200_mm: 0, 
    aisle_width_1000x1200_mm: 0,
    travel_speed_loaded_kmh: 0, 
    travel_speed_unloaded_kmh: 0,
    lift_speed_loaded_ms: 0, 
    lift_speed_unloaded_ms: 0,
    battery_voltage_v: 48, 
    battery_capacity_ah: 0, 
    image_url: '',
    mast_variants: [],
    battery_variants: [],
    residual_capacities: [],
    vna_specs: {},
    options: []
  };

  const [formDatas, setFormDatas] = useState([emptyForklift]);
  const [selectedModelIndex, setSelectedModelIndex] = useState(0);

  const [activeTab, setActiveTab] = useState('identification'); // identification, dimensions, battery, residual_capacities, vna_specs, performance, options

  useEffect(() => {
    fetchForklifts();
  }, []);

  const fetchForklifts = async () => {
    try {
      const res = await fetch('/api/forklifts');
      const data = await res.json();
      if (data.status === 'success') {
        setForklifts(data.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const currentData = formDatas[selectedModelIndex] || emptyForklift;

  const updateCurrentData = (field, value) => {
    setFormDatas(prev => {
      const newDatas = [...prev];
      newDatas[selectedModelIndex] = {
        ...newDatas[selectedModelIndex],
        [field]: value
      };
      return newDatas;
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    updateCurrentData(name, type === 'number' ? Number(value) : value);
  };

  const handleOpenModal = (forklift = null) => {
    if (forklift) {
      setEditingId(forklift.id);
      setFormDatas([{
        ...emptyForklift,
        ...forklift,
        mast_variants: Array.isArray(forklift.mast_variants) ? forklift.mast_variants : [],
        battery_variants: Array.isArray(forklift.battery_variants) ? forklift.battery_variants : [],
        residual_capacities: Array.isArray(forklift.residual_capacities) ? forklift.residual_capacities : [],
        vna_specs: forklift.vna_specs && typeof forklift.vna_specs === 'object' ? forklift.vna_specs : {},
        options: Array.isArray(forklift.options) ? forklift.options : []
      }]);
    } else {
      setEditingId(null);
      setFormDatas([{ ...emptyForklift }]);
    }
    setSelectedModelIndex(0);
    setActiveTab('identification');
    setNewOptionInput('');
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        // Update single item
        const res = await fetch(`/api/forklifts/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formDatas[0])
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.message || 'Failed to update forklift');
        }
      } else {
        // Create multiple items
        for (const data of formDatas) {
          const res = await fetch('/api/forklifts', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.message || `Failed to save forklift ${data.model}`);
          }
        }
      }
      setIsModalOpen(false);
      fetchForklifts();
    } catch (err) {
      console.error(err);
      alert('Error saving data: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      const res = await fetch(`/api/forklifts/${id}`, { method: 'DELETE' });
      if (res.ok) {
        fetchForklifts();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploadingImage(true);
    const data = new FormData();
    data.append('image', file);

    try {
      const res = await fetch('/api/upload', {
        method: 'POST',
        body: data,
      });
      const result = await res.json();
      if (result.status === 'success') {
        updateCurrentData('image_url', result.url);
      } else {
        alert('Upload failed: ' + result.message);
      }
    } catch (err) {
      console.error(err);
      alert('Network error during upload');
    } finally {
      setIsUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ---------------- Mast Variants helpers ----------------
  const addMastVariant = () => {
    const list = [...(currentData.mast_variants || [])];
    list.push({ 
      type: 'Triplex Hi-Lo', 
      lift_height_mm: currentData.lift_height_mm || 6000, 
      mast_lowered_mm: currentData.mast_lowered_mm || 2500, 
      free_lift_mm: currentData.free_lift_mm || 1800, 
      mast_extended_mm: currentData.mast_extended_mm || 6500 
    });
    updateCurrentData('mast_variants', list);
  };

  const updateMastVariant = (idx, key, val) => {
    const list = [...(currentData.mast_variants || [])];
    list[idx] = { ...list[idx], [key]: key === 'type' ? val : Number(val) };
    updateCurrentData('mast_variants', list);
  };

  const removeMastVariant = (idx) => {
    const list = [...(currentData.mast_variants || [])];
    list.splice(idx, 1);
    updateCurrentData('mast_variants', list);
  };

  // ---------------- Battery Variants helpers ----------------
  const addBatteryVariant = () => {
    const list = [...(currentData.battery_variants || [])];
    list.push({
      compartment: 'Standard',
      battery_type: 'Lead-acid',
      voltage_v: currentData.battery_voltage_v || 48,
      capacity_ah: currentData.battery_capacity_ah || 620,
      weight_kg: 850,
      wheelbase_mm: currentData.wheelbase_mm || 0,
      overall_length_mm: currentData.overall_length_mm || 0,
      length_to_fork_face_mm: currentData.length_to_fork_face_mm || 0,
      aisle_width_crossways_mm: currentData.aisle_width_1000x1200_mm || 0,
      aisle_width_lengthways_mm: currentData.aisle_width_800x1200_mm || 0,
      turning_radius_mm: currentData.turning_radius_mm || 0
    });
    updateCurrentData('battery_variants', list);
  };

  const updateBatteryVariant = (idx, key, val) => {
    const list = [...(currentData.battery_variants || [])];
    const isNum = ['voltage_v', 'capacity_ah', 'weight_kg', 'wheelbase_mm', 'overall_length_mm', 'length_to_fork_face_mm', 'aisle_width_crossways_mm', 'aisle_width_lengthways_mm', 'turning_radius_mm'].includes(key);
    list[idx] = { ...list[idx], [key]: isNum ? Number(val) : val };
    updateCurrentData('battery_variants', list);
  };

  const removeBatteryVariant = (idx) => {
    const list = [...(currentData.battery_variants || [])];
    list.splice(idx, 1);
    updateCurrentData('battery_variants', list);
  };

  // ---------------- Residual Capacities helpers ----------------
  const addResidualCapacity = () => {
    const list = [...(currentData.residual_capacities || [])];
    list.push({
      mast_type: 'Standard',
      lift_height_mm: currentData.lift_height_mm || 6000,
      load_centre_mm: currentData.load_centre_mm || 600,
      capacity_kg: currentData.load_capacity_kg || 1400
    });
    updateCurrentData('residual_capacities', list);
  };

  const updateResidualCapacity = (idx, key, val) => {
    const list = [...(currentData.residual_capacities || [])];
    const isNum = ['lift_height_mm', 'load_centre_mm', 'capacity_kg'].includes(key);
    list[idx] = { ...list[idx], [key]: isNum ? Number(val) : val };
    updateCurrentData('residual_capacities', list);
  };

  const removeResidualCapacity = (idx) => {
    const list = [...(currentData.residual_capacities || [])];
    list.splice(idx, 1);
    updateCurrentData('residual_capacities', list);
  };

  // ---------------- VNA Specs helpers ----------------
  const updateVnaSpec = (key, val) => {
    const currentSpecs = currentData.vna_specs || {};
    updateCurrentData('vna_specs', { ...currentSpecs, [key]: val });
  };

  // ---------------- Options helpers ----------------
  const addOption = () => {
    if (!newOptionInput.trim()) return;
    const list = [...(currentData.options || [])];
    if (!list.includes(newOptionInput.trim())) {
      list.push(newOptionInput.trim());
      updateCurrentData('options', list);
    }
    setNewOptionInput('');
  };

  const removeOption = (idx) => {
    const list = [...(currentData.options || [])];
    list.splice(idx, 1);
    updateCurrentData('options', list);
  };

  // ---------------- AI Auto-Fill ----------------
  const [extractionStep, setExtractionStep] = useState('idle');
  const [availableModels, setAvailableModels] = useState([]);
  const [uploadedFileBase64, setUploadedFileBase64] = useState(null);
  const [uploadedFileMime, setUploadedFileMime] = useState(null);

  const handleAutoFill = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setExtractionStep('scanning');
    try {
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = error => reject(error);
      });
      reader.readAsDataURL(file);
      const base64Data = await base64Promise;
      
      setUploadedFileBase64(base64Data);
      setUploadedFileMime(file.type);

      const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
      if (!apiKey) throw new Error("VITE_GEMINI_API_KEY is missing in environment");
      const genAI = new GoogleGenerativeAI(apiKey);
      const model = genAI.getGenerativeModel({ model: "gemini-3.8-flash", generationConfig: { responseMimeType: "application/json" } });

      const prompt = `
        You are a data extraction assistant. I am providing you with an image/pdf of a forklift datasheet (VDI 2198 standard).
        Please scan the document and list ALL FORKLIFT MODEL NAMES mentioned.
        Return ONLY a JSON ARRAY of strings. Example: ["RRE140H", "RRE160H", "RRE180H"]
      `;

      let result;
      for (let i = 0; i < 4; i++) {
        try {
          result = await model.generateContent([ prompt, { inlineData: { data: base64Data, mimeType: file.type } } ]);
          break;
        } catch (err) {
          if (err.message?.includes('429')) throw new Error("โควต้า API เต็มแล้วครับ (429) รบกวนรอสัก 1-2 นาที");
          if (err.message?.includes('503')) {
            if (i === 3) throw new Error("เซิร์ฟเวอร์ AI มีผู้ใช้งานเยอะชั่วคราว (503) โปรดลองใหม่");
            await new Promise(r => setTimeout(r, 2000 * Math.pow(1.5, i)));
          } else throw err;
        }
      }

      const text = result.response.text();
      let models = JSON.parse(text);
      if (!Array.isArray(models)) models = [models];
      
      setAvailableModels(models);
      setExtractionStep('selecting');

    } catch (err) {
      console.error(err);
      alert('Scanning failed: ' + err.message);
      setExtractionStep('idle');
    } finally {
      if (aiInputRef.current) aiInputRef.current.value = '';
    }
  };

  const handleExtractSpecificModel = async (modelName) => {
    setExtractionStep('extracting');
    try {
      const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
      const genAI = new GoogleGenerativeAI(apiKey);
      const model = genAI.getGenerativeModel({ model: "gemini-3.8-flash", generationConfig: { responseMimeType: "application/json" } });

      const prompt = `
        You are a data extraction assistant. I am providing you with an image/pdf of a forklift datasheet (VDI 2198 standard).
        Please extract technical specifications ONLY FOR MODEL: "${modelName}".
        Return the result as a single JSON object.
      `;

      let result;
      for (let i = 0; i < 4; i++) {
        try {
          result = await model.generateContent([ prompt, { inlineData: { data: uploadedFileBase64, mimeType: uploadedFileMime } } ]);
          break;
        } catch (err) {
          if (err.message?.includes('429')) throw new Error("โควต้า API เต็มแล้วครับ (429)");
          if (err.message?.includes('503')) {
            if (i === 3) throw new Error("เซิร์ฟเวอร์ไม่ตอบสนองชั่วคราว (503)");
            await new Promise(r => setTimeout(r, 2000 * Math.pow(1.5, i)));
          } else throw err;
        }
      }

      const text = result.response.text();
      let extractedData = JSON.parse(text);
      if (Array.isArray(extractedData)) extractedData = extractedData[0];
      
      setFormDatas(prev => {
        const newDatas = [...prev];
        newDatas[selectedModelIndex] = { ...emptyForklift, ...extractedData };
        return newDatas;
      });
      alert(`✅ Extracted data for ${modelName} successfully!`);
      setExtractionStep('idle');

    } catch (err) {
      console.error(err);
      alert('Extraction failed: ' + err.message);
      setExtractionStep('selecting');
    }
  };

  // Filter dropdown options
  const seriesOptions = ['ALL', ...Array.from(new Set(forklifts.map(f => f.series).filter(Boolean))).sort()];
  const categoryOptions = ['ALL', ...Array.from(new Set(forklifts.map(f => f.category).filter(Boolean))).sort()];

  // Filtered List
  const filteredForklifts = forklifts.filter(f => {
    const matchesSearch = 
      f.model?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      f.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (f.series && f.series.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (f.manufacturer && f.manufacturer.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesSeries = selectedSeriesFilter === 'ALL' || f.series === selectedSeriesFilter;
    const matchesCategory = selectedCategoryFilter === 'ALL' || f.category === selectedCategoryFilter;

    return matchesSearch && matchesSeries && matchesCategory;
  });

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategoryFilter, selectedSeriesFilter]);

  // Pagination calculation
  const totalPages = Math.ceil(filteredForklifts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedForklifts = filteredForklifts.slice(startIndex, startIndex + itemsPerPage);

  const tabDefs = [
    { id: 'identification', label: 'Identification & Specs' },
    { id: 'dimensions', label: `Dimensions (${currentData.mast_variants?.length || 0} Masts)` },
    { id: 'battery', label: `Battery (${currentData.battery_variants?.length || 0} Sizes)` },
    { id: 'residual_capacities', label: `Load Chart (${currentData.residual_capacities?.length || 0})` },
    { id: 'vna_specs', label: 'VNA Guide', highlight: currentData.category === 'Very Narrow Aisle' || (currentData.vna_specs && Object.keys(currentData.vna_specs).length > 0) },
    { id: 'performance', label: 'Performance' },
    { id: 'options', label: `Options (${currentData.options?.length || 0})` }
  ];

  return (
    <div className="p-6 bg-gray-50 h-full flex flex-col">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 flex-shrink-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Settings className="text-red-600" /> Product Settings (Forklifts)
          </h2>
          <p className="text-sm text-gray-500 mt-1">Toyota & BT Material Handling specification database with derating charts and battery variants.</p>
        </div>
        <button 
          onClick={() => handleOpenModal()}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium flex items-center gap-2 transition-colors shadow-sm"
        >
          <Plus size={18} /> Add New Product
        </button>
      </div>

      {/* Main Table Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col flex-1 min-h-0">
        {/* Filter Toolbar */}
        <div className="p-4 border-b border-gray-200 bg-gray-50/50 flex flex-wrap gap-3 justify-between items-center flex-shrink-0">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input 
                type="text"
                placeholder="Search models, series..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-red-500 focus:border-red-500 w-64 bg-white"
              />
            </div>

            {/* Category Filter */}
            <select
              value={selectedCategoryFilter}
              onChange={(e) => setSelectedCategoryFilter(e.target.value)}
              className="py-2 px-3 border border-gray-300 rounded-md text-sm bg-white focus:ring-red-500 focus:border-red-500"
            >
              <option value="ALL">All Categories ({categoryOptions.length - 1})</option>
              {categoryOptions.filter(c => c !== 'ALL').map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            {/* Series Filter */}
            <select
              value={selectedSeriesFilter}
              onChange={(e) => setSelectedSeriesFilter(e.target.value)}
              className="py-2 px-3 border border-gray-300 rounded-md text-sm bg-white focus:ring-red-500 focus:border-red-500 max-w-[220px]"
            >
              <option value="ALL">All Series ({seriesOptions.length - 1})</option>
              {seriesOptions.filter(s => s !== 'ALL').map(ser => (
                <option key={ser} value={ser}>{ser}</option>
              ))}
            </select>
          </div>

          <span className="text-sm text-gray-500 font-medium">
            Found: <strong className="text-gray-900">{filteredForklifts.length}</strong> models (Total DB: {forklifts.length})
          </span>
        </div>

        {/* Product Table */}
        <div className="overflow-x-auto flex-1 overflow-y-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 sticky top-0 z-10 shadow-xs">
              <tr>
                <th className="px-5 py-3 bg-gray-50">Image</th>
                <th className="px-5 py-3 bg-gray-50">Model & Series</th>
                <th className="px-5 py-3 bg-gray-50">Category</th>
                <th className="px-5 py-3 bg-gray-50">Capacity (kg)</th>
                <th className="px-5 py-3 bg-gray-50">Lift Height (mm)</th>
                <th className="px-5 py-3 bg-gray-50">Aisle Ast</th>
                <th className="px-5 py-3 bg-gray-50">Rich Specs</th>
                <th className="px-5 py-3 text-right bg-gray-50">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr><td colSpan="8" className="px-6 py-12 text-center text-gray-500">Loading catalog data...</td></tr>
              ) : filteredForklifts.length === 0 ? (
                <tr><td colSpan="8" className="px-6 py-12 text-center text-gray-500">No products match your filters.</td></tr>
              ) : (
                paginatedForklifts.map(f => (
                  <tr key={f.id} className="hover:bg-gray-50/80 transition-colors">
                    <td className="px-5 py-2">
                      {f.image_url ? (
                        <img src={f.image_url} alt={f.model} className="h-10 w-10 object-contain rounded border bg-white" />
                      ) : (
                        <div className="h-10 w-10 bg-gray-100 rounded border flex items-center justify-center text-gray-400">
                          <ImageIcon size={16} />
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 font-medium text-gray-900">
                      <div className="font-semibold text-gray-900">{f.manufacturer} {f.model}</div>
                      {f.series ? (
                        <span className="inline-block mt-0.5 text-[11px] font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded border border-gray-200">
                          {f.series}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Standard</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                        f.category === 'Reach truck' ? 'bg-blue-100 text-blue-800' :
                        f.category === 'Very Narrow Aisle' ? 'bg-purple-100 text-purple-800' :
                        f.category === 'Electric counterbalance' ? 'bg-emerald-100 text-emerald-800' :
                        f.category === 'Engine counterbalance' ? 'bg-orange-100 text-orange-800' :
                        f.category === 'Pallet stacker' ? 'bg-amber-100 text-amber-800' :
                        f.category === 'Pallet truck' ? 'bg-teal-100 text-teal-800' :
                        f.category === 'Order picker' ? 'bg-cyan-100 text-cyan-800' :
                        f.category === 'Automated truck' ? 'bg-indigo-100 text-indigo-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {f.category}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-medium">{f.load_capacity_kg ? `${f.load_capacity_kg.toLocaleString()} kg` : '-'}</td>
                    <td className="px-5 py-3">
                      <div>{f.lift_height_mm ? `${f.lift_height_mm.toLocaleString()} mm` : '-'}</div>
                      {f.mast_variants && f.mast_variants.length > 0 && (
                        <span className="text-[11px] text-gray-500 font-normal">+{f.mast_variants.length} masts</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-gray-600 text-xs">
                      {f.aisle_width_1000x1200_mm ? `Ast: ${f.aisle_width_1000x1200_mm} mm` : '-'}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1">
                        {f.battery_variants && f.battery_variants.length > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-200" title={`${f.battery_variants.length} battery compartments`}>
                            <Battery size={10} /> {f.battery_variants.length}
                          </span>
                        )}
                        {f.residual_capacities && f.residual_capacities.length > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200" title={`${f.residual_capacities.length} derating points`}>
                            <BarChart2 size={10} /> Chart ({f.residual_capacities.length})
                          </span>
                        )}
                        {f.vna_specs && Object.keys(f.vna_specs).length > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded border border-purple-200" title="VNA guidance specifications">
                            <Compass size={10} /> VNA
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => handleOpenModal(f)} className="text-blue-600 hover:text-blue-900 mr-2 p-1.5 rounded hover:bg-blue-50 transition-colors" title="Edit specification">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => handleDelete(f.id)} className="text-red-600 hover:text-red-900 p-1.5 rounded hover:bg-red-50 transition-colors" title="Delete model">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {totalPages > 1 && (
          <div className="p-3 border-t border-gray-200 bg-white flex flex-col sm:flex-row justify-between items-center gap-3 flex-shrink-0">
            <div className="text-xs text-gray-500">
              Showing <strong className="text-gray-900">{startIndex + 1}</strong> to <strong className="text-gray-900">{Math.min(startIndex + itemsPerPage, filteredForklifts.length)}</strong> of <strong className="text-gray-900">{filteredForklifts.length}</strong> models
            </div>
            
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-2.5 py-1 text-xs border rounded-md font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Prev
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .map((p, idx, arr) => {
                  const showEllipsis = idx > 0 && p - arr[idx - 1] > 1;
                  return (
                    <React.Fragment key={p}>
                      {showEllipsis && <span className="px-1 text-xs text-gray-400">...</span>}
                      <button
                        onClick={() => setCurrentPage(p)}
                        className={`px-2.5 py-1 text-xs border rounded-md font-semibold transition-colors ${
                          currentPage === p
                            ? 'bg-red-600 text-white border-red-600'
                            : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-2.5 py-1 text-xs border rounded-md font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto p-4 md:p-6">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col relative overflow-hidden">
            
            {/* Modal Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b bg-gray-50/70">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 text-red-600 rounded-lg">
                  <Settings size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {editingId ? `Edit Product: ${currentData.model || 'Forklift'}` : 'Add New Product Specification'}
                  </h3>
                  <p className="text-xs text-gray-500">Configure dimensional parameters, battery sizes, derating chart, and options.</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input 
                  type="file" 
                  ref={aiInputRef}
                  accept="image/*,application/pdf"
                  className="hidden" 
                  onChange={handleAutoFill} 
                />
                <button 
                  onClick={() => aiInputRef.current.click()}
                  disabled={extractionStep !== 'idle'}
                  className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 rounded-lg text-xs font-semibold transition-colors"
                >
                  {extractionStep !== 'idle' ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
                  {extractionStep === 'scanning' ? 'Scanning...' : 
                   extractionStep === 'extracting' ? 'Extracting...' : 
                   extractionStep === 'selecting' ? 'Waiting...' : 
                   '🪄 Auto-Fill from PDF/Image'}
                </button>
                <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-700 p-1 rounded-md">
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Selection Modal for AI Scanning */}
            {extractionStep === 'selecting' && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
                <div className="bg-white rounded-xl p-6 shadow-2xl border border-gray-200 w-96 max-w-full">
                  <h4 className="text-lg font-bold mb-2">Select Model to Extract</h4>
                  <p className="text-xs text-gray-600 mb-4">Gemini scanned these models in the uploaded document. Select one to populate fields:</p>
                  <div className="space-y-2 max-h-60 overflow-y-auto mb-4 p-1">
                    {availableModels.length === 0 ? (
                      <p className="text-xs text-gray-500 italic">No models identified.</p>
                    ) : (
                      availableModels.map(m => (
                        <button
                          key={m}
                          onClick={() => handleExtractSpecificModel(m)}
                          className="w-full text-left px-4 py-2.5 border rounded-lg hover:bg-indigo-50 hover:border-indigo-300 font-medium text-xs text-gray-800 transition-colors flex items-center justify-between group"
                        >
                          {m}
                          <Wand2 size={14} className="text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      ))
                    )}
                  </div>
                  <div className="flex justify-end pt-3 border-t">
                    <button 
                      onClick={() => setExtractionStep('idle')}
                      className="px-4 py-1.5 text-xs text-gray-600 font-medium hover:bg-gray-100 rounded-md"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Batch Navigation (if adding multiple) */}
            <div className="bg-gray-100 px-6 py-2 border-b flex items-center overflow-x-auto gap-2">
              {formDatas.map((data, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedModelIndex(idx)}
                  className={`px-3 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                    selectedModelIndex === idx 
                      ? 'bg-white text-red-600 shadow-sm border border-gray-200' 
                      : 'bg-transparent text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {idx + 1}. {data.model || 'New Model'}
                </button>
              ))}
              {!editingId && (
                <button 
                  onClick={() => {
                    setFormDatas(prev => [...prev, { ...emptyForklift }]);
                    setSelectedModelIndex(formDatas.length);
                  }}
                  className="px-2 py-1 rounded text-gray-500 hover:bg-gray-200 hover:text-gray-700 flex items-center gap-1 text-xs"
                  title="Add another model manually"
                >
                  <Plus size={14} /> Add Model Tab
                </button>
              )}
            </div>

            {/* Modal Tabs Bar */}
            <div className="flex border-b px-6 bg-white overflow-x-auto gap-1">
              {tabDefs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 font-medium text-xs whitespace-nowrap border-b-2 transition-colors flex items-center gap-1.5 ${
                    activeTab === tab.id 
                      ? 'border-red-600 text-red-600 font-semibold' 
                      : tab.highlight 
                        ? 'border-transparent text-purple-700 hover:text-purple-900 bg-purple-50/50' 
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.id === 'battery' && <Battery size={14} />}
                  {tab.id === 'residual_capacities' && <BarChart2 size={14} />}
                  {tab.id === 'dimensions' && <Layers size={14} />}
                  {tab.id === 'vna_specs' && <Compass size={14} />}
                  {tab.id === 'options' && <Shield size={14} />}
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Modal Body / Tab Content */}
            <div className="p-6 overflow-y-auto flex-1 bg-white">
              <form id="productForm" onSubmit={handleSave} className="space-y-4">
                
                {/* 1. Identification & General */}
                <div className={activeTab === 'identification' ? 'block' : 'hidden'}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    {/* Image Upload Area */}
                    <div className="col-span-1 md:col-span-3 flex items-center gap-4 p-4 border border-dashed border-gray-300 rounded-xl bg-gray-50">
                      {currentData.image_url ? (
                        <img src={currentData.image_url} alt="Preview" className="h-20 w-20 object-contain bg-white border rounded-lg p-1" />
                      ) : (
                        <div className="h-20 w-20 bg-gray-200 flex items-center justify-center rounded-lg border text-gray-400">
                          <ImageIcon size={28} />
                        </div>
                      )}
                      <div className="flex-1">
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Product Illustration</label>
                        <input 
                          type="file" 
                          ref={fileInputRef}
                          accept="image/*"
                          className="hidden" 
                          onChange={handleImageUpload} 
                        />
                        <button 
                          type="button"
                          onClick={() => fileInputRef.current.click()}
                          disabled={isUploadingImage}
                          className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-md shadow-xs text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          {isUploadingImage ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                          {isUploadingImage ? 'Uploading...' : 'Upload Image (PNG/JPG)'}
                        </button>
                        <p className="text-[11px] text-gray-500 mt-1">Recommended transparent PNG for 2D/3D map preview.</p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700">Manufacturer</label>
                      <input type="text" name="manufacturer" value={currentData.manufacturer} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded-md text-sm" required />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700">Model Name (e.g. RRE160H)</label>
                      <input type="text" name="model" value={currentData.model} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded-md text-sm font-semibold bg-yellow-50/50" required />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700">Series / Line-up (e.g. BT Reflex R-series)</label>
                      <input type="text" name="series" value={currentData.series || ''} onChange={handleInputChange} placeholder="e.g. BT Reflex R-series" className="mt-1 w-full p-2 border rounded-md text-sm" />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700">Category</label>
                      <select name="category" value={currentData.category} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded-md text-sm">
                        <option value="Reach truck">Reach truck</option>
                        <option value="Electric counterbalance">Electric counterbalance</option>
                        <option value="Engine counterbalance">Engine counterbalance</option>
                        <option value="Very Narrow Aisle">Very Narrow Aisle (VNA)</option>
                        <option value="Pallet stacker">Pallet stacker</option>
                        <option value="Pallet truck">Pallet truck</option>
                        <option value="Order picker">Order picker</option>
                        <option value="Towing tractor">Towing tractor</option>
                        <option value="Automated truck">Automated truck</option>
                        <option value="Hand pallet truck">Hand pallet truck</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700">Drive Type</label>
                      <input type="text" name="drive_type" value={currentData.drive_type} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded-md text-sm" placeholder="Electric, LPG, Diesel" />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700">Operator Type</label>
                      <input type="text" name="operator_type" value={currentData.operator_type} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded-md text-sm" placeholder="Seated, Stand-on, Pedestrian" />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700">Load Capacity Q (kg)</label>
                      <input type="number" name="load_capacity_kg" value={currentData.load_capacity_kg} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded-md text-sm font-semibold bg-yellow-50" />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700">Load Centre c (mm)</label>
                      <input type="number" name="load_centre_mm" value={currentData.load_centre_mm} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded-md text-sm" />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700">Wheelbase y (mm)</label>
                      <input type="number" name="wheelbase_mm" value={currentData.wheelbase_mm} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded-md text-sm" />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700">Service Weight (kg)</label>
                      <input type="number" name="service_weight_kg" value={currentData.service_weight_kg} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded-md text-sm" />
                    </div>
                  </div>
                </div>

                {/* 2. Dimensions & Masts */}
                <div className={activeTab === 'dimensions' ? 'block' : 'hidden'}>
                  <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider mb-2">Standard Truck Dimensions</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div><label className="block text-[11px] font-medium text-gray-700">Mast Lowered h1 (mm)</label><input type="number" name="mast_lowered_mm" value={currentData.mast_lowered_mm} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded text-xs" /></div>
                    <div><label className="block text-[11px] font-medium text-gray-700">Free Lift h2 (mm)</label><input type="number" name="free_lift_mm" value={currentData.free_lift_mm} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded text-xs" /></div>
                    <div><label className="block text-[11px] font-medium text-gray-700">Lift Height h3/h23 (mm)</label><input type="number" name="lift_height_mm" value={currentData.lift_height_mm} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded text-xs bg-yellow-50" /></div>
                    <div><label className="block text-[11px] font-medium text-gray-700">Mast Extended h4 (mm)</label><input type="number" name="mast_extended_mm" value={currentData.mast_extended_mm} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded text-xs" /></div>
                    <div><label className="block text-[11px] font-medium text-gray-700">Overall Length l1 (mm)</label><input type="number" name="overall_length_mm" value={currentData.overall_length_mm} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded text-xs" /></div>
                    <div><label className="block text-[11px] font-medium text-gray-700">Length to Face l2 (mm)</label><input type="number" name="length_to_fork_face_mm" value={currentData.length_to_fork_face_mm} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded text-xs" /></div>
                    <div><label className="block text-[11px] font-medium text-gray-700">Overall Width b1/b2 (mm)</label><input type="number" name="overall_width_mm" value={currentData.overall_width_mm} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded text-xs" /></div>
                    <div><label className="block text-[11px] font-medium text-gray-700">Turning Radius Wa (mm)</label><input type="number" name="turning_radius_mm" value={currentData.turning_radius_mm} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded text-xs bg-yellow-50" /></div>
                    <div className="col-span-2"><label className="block text-[11px] font-medium text-gray-700">Aisle Width Ast 1000x1200 Crossways (mm)</label><input type="number" name="aisle_width_1000x1200_mm" value={currentData.aisle_width_1000x1200_mm} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded text-xs bg-blue-50 font-semibold" /></div>
                    <div className="col-span-2"><label className="block text-[11px] font-medium text-gray-700">Aisle Width Ast 800x1200 Lengthways (mm)</label><input type="number" name="aisle_width_800x1200_mm" value={currentData.aisle_width_800x1200_mm} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded text-xs bg-blue-50 font-semibold" /></div>
                  </div>
                  
                  {/* Mast Variants Section */}
                  <div className="mt-6">
                    <div className="flex justify-between items-center mb-2 pb-1 border-b">
                      <div>
                        <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider">Mast Variants (h23 / Lift Heights)</h4>
                        <p className="text-[11px] text-gray-500">Available mast heights to select during proposal generation</p>
                      </div>
                      <button 
                        type="button" 
                        onClick={addMastVariant}
                        className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 font-semibold px-2 py-1 bg-red-50 rounded border border-red-200"
                      >
                        <Plus size={14} /> Add Mast Variant
                      </button>
                    </div>

                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                      <table className="w-full text-xs text-left">
                        <thead className="bg-gray-50 text-gray-700 uppercase">
                          <tr>
                            <th className="px-3 py-2">Mast Type</th>
                            <th className="px-3 py-2">Lift (h3/h23)</th>
                            <th className="px-3 py-2">Lowered (h1)</th>
                            <th className="px-3 py-2">Free Lift (h2)</th>
                            <th className="px-3 py-2">Extended (h4)</th>
                            <th className="px-3 py-2 text-right">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {(!currentData.mast_variants || currentData.mast_variants.length === 0) ? (
                            <tr><td colSpan="6" className="px-3 py-4 text-center text-gray-400 italic">No mast variants defined. Click "Add Mast Variant" above.</td></tr>
                          ) : (
                            currentData.mast_variants.map((mv, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-2 py-1.5">
                                  <input 
                                    type="text" 
                                    value={mv.type || ''} 
                                    onChange={(e) => updateMastVariant(idx, 'type', e.target.value)} 
                                    className="w-full p-1 border rounded text-xs" 
                                    placeholder="Triplex Hi-Lo"
                                  />
                                </td>
                                <td className="px-2 py-1.5">
                                  <input 
                                    type="number" 
                                    value={mv.lift_height_mm || 0} 
                                    onChange={(e) => updateMastVariant(idx, 'lift_height_mm', e.target.value)} 
                                    className="w-20 p-1 border rounded text-xs bg-yellow-50 font-medium" 
                                  />
                                </td>
                                <td className="px-2 py-1.5">
                                  <input 
                                    type="number" 
                                    value={mv.mast_lowered_mm || 0} 
                                    onChange={(e) => updateMastVariant(idx, 'mast_lowered_mm', e.target.value)} 
                                    className="w-20 p-1 border rounded text-xs" 
                                  />
                                </td>
                                <td className="px-2 py-1.5">
                                  <input 
                                    type="number" 
                                    value={mv.free_lift_mm || 0} 
                                    onChange={(e) => updateMastVariant(idx, 'free_lift_mm', e.target.value)} 
                                    className="w-20 p-1 border rounded text-xs" 
                                  />
                                </td>
                                <td className="px-2 py-1.5">
                                  <input 
                                    type="number" 
                                    value={mv.mast_extended_mm || 0} 
                                    onChange={(e) => updateMastVariant(idx, 'mast_extended_mm', e.target.value)} 
                                    className="w-20 p-1 border rounded text-xs" 
                                  />
                                </td>
                                <td className="px-2 py-1.5 text-right">
                                  <button 
                                    type="button" 
                                    onClick={() => removeMastVariant(idx)} 
                                    className="text-red-500 hover:text-red-700 p-1"
                                  >
                                    <X size={14} />
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* 3. Battery & Compartments */}
                <div className={activeTab === 'battery' ? 'block' : 'hidden'}>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700">Standard Battery Voltage (V)</label>
                      <input type="number" name="battery_voltage_v" value={currentData.battery_voltage_v} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded-md text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700">Standard Battery Capacity (Ah)</label>
                      <input type="number" name="battery_capacity_ah" value={currentData.battery_capacity_ah} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded-md text-sm" />
                    </div>
                  </div>

                  <div className="flex justify-between items-center mb-2 pb-1 border-b">
                    <div>
                      <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider">Battery Compartment Variants</h4>
                      <p className="text-[11px] text-gray-500">Different battery sizes impact truck length (l1, l2), turning radius (Wa), and aisle widths (Ast).</p>
                    </div>
                    <button 
                      type="button" 
                      onClick={addBatteryVariant}
                      className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 font-semibold px-2 py-1 bg-red-50 rounded border border-red-200"
                    >
                      <Plus size={14} /> Add Battery Compartment
                    </button>
                  </div>

                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-gray-50 text-gray-700 uppercase">
                        <tr>
                          <th className="px-2 py-2">Compartment</th>
                          <th className="px-2 py-2">Type</th>
                          <th className="px-2 py-2">Cap (Ah)</th>
                          <th className="px-2 py-2">Weight (kg)</th>
                          <th className="px-2 py-2">Overall l1</th>
                          <th className="px-2 py-2">Face l2</th>
                          <th className="px-2 py-2">Ast Cross</th>
                          <th className="px-2 py-2">Ast Length</th>
                          <th className="px-2 py-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(!currentData.battery_variants || currentData.battery_variants.length === 0) ? (
                          <tr><td colSpan="9" className="px-3 py-4 text-center text-gray-400 italic">No battery variants defined. Click "Add Battery Compartment" above.</td></tr>
                        ) : (
                          currentData.battery_variants.map((bv, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-1.5 py-1">
                                <input 
                                  type="text" 
                                  value={bv.compartment || ''} 
                                  onChange={(e) => updateBatteryVariant(idx, 'compartment', e.target.value)} 
                                  className="w-24 p-1 border rounded text-xs" 
                                  placeholder="275 mm"
                                />
                              </td>
                              <td className="px-1.5 py-1">
                                <input 
                                  type="text" 
                                  value={bv.battery_type || ''} 
                                  onChange={(e) => updateBatteryVariant(idx, 'battery_type', e.target.value)} 
                                  className="w-20 p-1 border rounded text-xs" 
                                  placeholder="Lead-acid"
                                />
                              </td>
                              <td className="px-1.5 py-1">
                                <input 
                                  type="number" 
                                  value={bv.capacity_ah || 0} 
                                  onChange={(e) => updateBatteryVariant(idx, 'capacity_ah', e.target.value)} 
                                  className="w-16 p-1 border rounded text-xs bg-yellow-50 font-medium" 
                                />
                              </td>
                              <td className="px-1.5 py-1">
                                <input 
                                  type="number" 
                                  value={bv.weight_kg || 0} 
                                  onChange={(e) => updateBatteryVariant(idx, 'weight_kg', e.target.value)} 
                                  className="w-16 p-1 border rounded text-xs" 
                                />
                              </td>
                              <td className="px-1.5 py-1">
                                <input 
                                  type="number" 
                                  value={bv.overall_length_mm || 0} 
                                  onChange={(e) => updateBatteryVariant(idx, 'overall_length_mm', e.target.value)} 
                                  className="w-16 p-1 border rounded text-xs" 
                                />
                              </td>
                              <td className="px-1.5 py-1">
                                <input 
                                  type="number" 
                                  value={bv.length_to_fork_face_mm || 0} 
                                  onChange={(e) => updateBatteryVariant(idx, 'length_to_fork_face_mm', e.target.value)} 
                                  className="w-16 p-1 border rounded text-xs" 
                                />
                              </td>
                              <td className="px-1.5 py-1">
                                <input 
                                  type="number" 
                                  value={bv.aisle_width_crossways_mm || 0} 
                                  onChange={(e) => updateBatteryVariant(idx, 'aisle_width_crossways_mm', e.target.value)} 
                                  className="w-16 p-1 border rounded text-xs bg-blue-50 font-medium" 
                                />
                              </td>
                              <td className="px-1.5 py-1">
                                <input 
                                  type="number" 
                                  value={bv.aisle_width_lengthways_mm || 0} 
                                  onChange={(e) => updateBatteryVariant(idx, 'aisle_width_lengthways_mm', e.target.value)} 
                                  className="w-16 p-1 border rounded text-xs bg-blue-50 font-medium" 
                                />
                              </td>
                              <td className="px-1.5 py-1 text-right">
                                <button 
                                  type="button" 
                                  onClick={() => removeBatteryVariant(idx)} 
                                  className="text-red-500 hover:text-red-700 p-1"
                                >
                                  <X size={14} />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 4. Residual Capacity Chart */}
                <div className={activeTab === 'residual_capacities' ? 'block' : 'hidden'}>
                  <div className="flex justify-between items-center mb-2 pb-1 border-b">
                    <div>
                      <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider">Residual Capacities / Derating Load Chart</h4>
                      <p className="text-[11px] text-gray-500">
                        Nominal Capacity: <strong className="text-gray-900">{currentData.load_capacity_kg || 0} kg</strong> @ {currentData.load_centre_mm || 600} mm. Shows safe lifting limits at higher mast elevations.
                      </p>
                    </div>
                    <button 
                      type="button" 
                      onClick={addResidualCapacity}
                      className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 font-semibold px-2 py-1 bg-red-50 rounded border border-red-200"
                    >
                      <Plus size={14} /> Add Derating Point
                    </button>
                  </div>

                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-gray-50 text-gray-700 uppercase">
                        <tr>
                          <th className="px-3 py-2">Mast Type (Optional)</th>
                          <th className="px-3 py-2">Lift Height (mm)</th>
                          <th className="px-3 py-2">Load Centre (mm)</th>
                          <th className="px-3 py-2">Residual Capacity (kg)</th>
                          <th className="px-3 py-2">Safety Derating Status</th>
                          <th className="px-3 py-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(!currentData.residual_capacities || currentData.residual_capacities.length === 0) ? (
                          <tr><td colSpan="6" className="px-3 py-4 text-center text-gray-400 italic">No derating data recorded. Click "Add Derating Point" above.</td></tr>
                        ) : (
                          currentData.residual_capacities.map((rc, idx) => {
                            const nominal = currentData.load_capacity_kg || 1;
                            const ratio = rc.capacity_kg ? (rc.capacity_kg / nominal) * 100 : 100;
                            const isDerated = ratio < 99.5;
                            return (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-2 py-1.5">
                                  <input 
                                    type="text" 
                                    value={rc.mast_type || ''} 
                                    onChange={(e) => updateResidualCapacity(idx, 'mast_type', e.target.value)} 
                                    className="w-28 p-1 border rounded text-xs" 
                                    placeholder="Triplex / FSV"
                                  />
                                </td>
                                <td className="px-2 py-1.5">
                                  <input 
                                    type="number" 
                                    value={rc.lift_height_mm || 0} 
                                    onChange={(e) => updateResidualCapacity(idx, 'lift_height_mm', e.target.value)} 
                                    className="w-24 p-1 border rounded text-xs bg-yellow-50 font-medium" 
                                  />
                                </td>
                                <td className="px-2 py-1.5">
                                  <input 
                                    type="number" 
                                    value={rc.load_centre_mm || 600} 
                                    onChange={(e) => updateResidualCapacity(idx, 'load_centre_mm', e.target.value)} 
                                    className="w-20 p-1 border rounded text-xs" 
                                  />
                                </td>
                                <td className="px-2 py-1.5">
                                  <input 
                                    type="number" 
                                    value={rc.capacity_kg || 0} 
                                    onChange={(e) => updateResidualCapacity(idx, 'capacity_kg', e.target.value)} 
                                    className="w-24 p-1 border rounded text-xs font-semibold text-gray-900" 
                                  />
                                </td>
                                <td className="px-2 py-1.5">
                                  {isDerated ? (
                                    <span className="inline-flex items-center text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                                      Derated to {rc.capacity_kg} kg ({Math.round(ratio)}%)
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                                      100% Full Nominal Load
                                    </span>
                                  )}
                                </td>
                                <td className="px-2 py-1.5 text-right">
                                  <button 
                                    type="button" 
                                    onClick={() => removeResidualCapacity(idx)} 
                                    className="text-red-500 hover:text-red-700 p-1"
                                  >
                                    <X size={14} />
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 5. VNA Specifications */}
                <div className={activeTab === 'vna_specs' ? 'block' : 'hidden'}>
                  <div className="bg-purple-50/70 border border-purple-200 rounded-xl p-4 mb-4">
                    <h4 className="text-xs font-bold text-purple-900 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                      <Compass size={16} /> Very Narrow Aisle (VNA) Architecture
                    </h4>
                    <p className="text-xs text-purple-700">Detailed parameters for high-density narrow aisle trucks (man-up picking, guidance rails/wires, and transfer aisles).</p>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border">
                      <input 
                        type="checkbox" 
                        id="vna_man_up"
                        checked={!!currentData.vna_specs?.man_up} 
                        onChange={(e) => updateVnaSpec('man_up', e.target.checked)} 
                        className="rounded text-red-600 focus:ring-red-500 h-4 w-4"
                      />
                      <label htmlFor="vna_man_up" className="text-xs font-semibold text-gray-800 cursor-pointer">Man-Up Cabin (Order Picking)</label>
                    </div>

                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border">
                      <input 
                        type="checkbox" 
                        id="vna_articulated"
                        checked={!!currentData.vna_specs?.articulated_chassis} 
                        onChange={(e) => updateVnaSpec('articulated_chassis', e.target.checked)} 
                        className="rounded text-red-600 focus:ring-red-500 h-4 w-4"
                      />
                      <label htmlFor="vna_articulated" className="text-xs font-semibold text-gray-800 cursor-pointer">Articulated Chassis</label>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700">Max Picking Height h28 (mm)</label>
                      <input 
                        type="number" 
                        value={currentData.vna_specs?.max_picking_height_mm || 0} 
                        onChange={(e) => updateVnaSpec('max_picking_height_mm', Number(e.target.value))} 
                        className="mt-1 w-full p-2 border rounded text-xs bg-yellow-50 font-medium" 
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700">Max Platform Height h12 (mm)</label>
                      <input 
                        type="number" 
                        value={currentData.vna_specs?.max_platform_height_mm || 0} 
                        onChange={(e) => updateVnaSpec('max_platform_height_mm', Number(e.target.value))} 
                        className="mt-1 w-full p-2 border rounded text-xs" 
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700">Auxiliary Mast Lift (mm)</label>
                      <input 
                        type="number" 
                        value={currentData.vna_specs?.auxiliary_mast_lift_mm || 0} 
                        onChange={(e) => updateVnaSpec('auxiliary_mast_lift_mm', Number(e.target.value))} 
                        className="mt-1 w-full p-2 border rounded text-xs" 
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700">Practical Transfer Aisle Ast3 (mm)</label>
                      <input 
                        type="number" 
                        value={currentData.vna_specs?.transfer_aisle_practical_mm || 0} 
                        onChange={(e) => updateVnaSpec('transfer_aisle_practical_mm', Number(e.target.value))} 
                        className="mt-1 w-full p-2 border rounded text-xs bg-blue-50 font-semibold" 
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700">Theoretical Transfer Aisle Ast3 (mm)</label>
                      <input 
                        type="number" 
                        value={currentData.vna_specs?.transfer_aisle_theoretical_mm || 0} 
                        onChange={(e) => updateVnaSpec('transfer_aisle_theoretical_mm', Number(e.target.value))} 
                        className="mt-1 w-full p-2 border rounded text-xs" 
                      />
                    </div>
                  </div>
                </div>

                {/* 6. Performance */}
                <div className={activeTab === 'performance' ? 'block' : 'hidden'}>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-xs font-medium text-gray-700">Travel Speed Loaded (km/h)</label><input type="number" step="0.1" name="travel_speed_loaded_kmh" value={currentData.travel_speed_loaded_kmh} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded text-sm" /></div>
                    <div><label className="block text-xs font-medium text-gray-700">Travel Speed Unloaded (km/h)</label><input type="number" step="0.1" name="travel_speed_unloaded_kmh" value={currentData.travel_speed_unloaded_kmh} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded text-sm" /></div>
                    <div><label className="block text-xs font-medium text-gray-700">Lift Speed Loaded (m/s)</label><input type="number" step="0.01" name="lift_speed_loaded_ms" value={currentData.lift_speed_loaded_ms} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded text-sm" /></div>
                    <div><label className="block text-xs font-medium text-gray-700">Lift Speed Unloaded (m/s)</label><input type="number" step="0.01" name="lift_speed_unloaded_ms" value={currentData.lift_speed_unloaded_ms} onChange={handleInputChange} className="mt-1 w-full p-2 border rounded text-sm" /></div>
                  </div>
                </div>

                {/* 7. Options & Features */}
                <div className={activeTab === 'options' ? 'block' : 'hidden'}>
                  <div className="flex justify-between items-center mb-3 pb-1 border-b">
                    <div>
                      <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider">Standard & Optional Equipment</h4>
                      <p className="text-[11px] text-gray-500">Key safety systems, driving controls, and accessories included with this model.</p>
                    </div>
                  </div>

                  <div className="flex gap-2 mb-4">
                    <input 
                      type="text" 
                      value={newOptionInput} 
                      onChange={(e) => setNewOptionInput(e.target.value)} 
                      placeholder="e.g. Transitional Lift Control (TLC), Toyota SAS..." 
                      className="flex-1 p-2 border rounded-md text-sm"
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }}
                    />
                    <button 
                      type="button" 
                      onClick={addOption}
                      className="px-4 py-2 bg-gray-900 text-white rounded-md text-xs font-semibold hover:bg-black transition-colors"
                    >
                      Add Option
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2 p-4 bg-gray-50 rounded-xl border min-h-[100px]">
                    {(!currentData.options || currentData.options.length === 0) ? (
                      <p className="text-xs text-gray-400 italic">No options added yet. Type an option name and click "Add Option".</p>
                    ) : (
                      currentData.options.map((opt, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-200 rounded-full text-xs font-medium text-gray-800 shadow-2xs">
                          {opt}
                          <button 
                            type="button" 
                            onClick={() => removeOption(idx)} 
                            className="text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                </div>
              </form>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t flex justify-end gap-3 bg-gray-50">
              <button 
                type="button" 
                onClick={() => setIsModalOpen(false)} 
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 text-xs font-semibold hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                form="productForm" 
                className="px-5 py-2 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700 shadow-sm transition-colors"
              >
                {editingId 
                  ? 'Update Product' 
                  : formDatas.length > 1 
                    ? `Save All Products (${formDatas.length})` 
                    : 'Save Product'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
